import React, { useState, useEffect, useCallback } from 'react';
import type { ForexRate } from '../../../types/dashboard';

interface EnhancedForexTableProps {
  className?: string;
}

const EnhancedForexTable: React.FC<EnhancedForexTableProps> = ({ className = '' }) => {
  const [rates, setRates] = useState<ForexRate[]>([]);
  const [flashingCells, setFlashingCells] = useState<Set<string>>(new Set());

  // Initial forex data matching the image exactly
  const initialForexData: ForexRate[] = [
    {
      id: 'usd',
      country: 'us United States',
      countryCode: 'us',
      currency: 'US Dollar',
      symbol: 'USD',
      phpRate: 57.36,
      change: 0.12,
      changePercent: 0.21,
      lastUpdated: new Date(),
      trend: 'up'
    },
    {
      id: 'eur',
      country: 'eu European Union',
      countryCode: 'eu',
      currency: 'Euro',
      symbol: 'EUR',
      phpRate: 62.80,
      change: -0.25,
      changePercent: -0.40,
      lastUpdated: new Date(),
      trend: 'down'
    },
    {
      id: 'jpy',
      country: 'jp Japan',
      countryCode: 'jp',
      currency: 'Japanese Yen',
      symbol: 'JPY',
      phpRate: 0.38,
      change: 0.01,
      changePercent: 2.63,
      lastUpdated: new Date(),
      trend: 'up'
    },
    {
      id: 'gbp',
      country: 'gb United Kingdom',
      countryCode: 'gb',
      currency: 'British Pound',
      symbol: 'GBP',
      phpRate: 72.45,
      change: 0.33,
      changePercent: 0.46,
      lastUpdated: new Date(),
      trend: 'up'
    },
    {
      id: 'cad',
      country: 'ca Canada',
      countryCode: 'ca',
      currency: 'Canadian Dollar',
      symbol: 'CAD',
      phpRate: 42.18,
      change: -0.08,
      changePercent: -0.19,
      lastUpdated: new Date(),
      trend: 'down'
    },
    {
      id: 'aud',
      country: 'au Australia',
      countryCode: 'au',
      currency: 'Australian Dollar',
      symbol: 'AUD',
      phpRate: 38.92,
      change: 0.15,
      changePercent: 0.39,
      lastUpdated: new Date(),
      trend: 'up'
    },
    {
      id: 'sgd',
      country: 'sg Singapore',
      countryCode: 'sg',
      currency: 'Singapore Dollar',
      symbol: 'SGD',
      phpRate: 42.67,
      change: -0.05,
      changePercent: -0.12,
      lastUpdated: new Date(),
      trend: 'down'
    },
    {
      id: 'krw',
      country: 'kr South Korea',
      countryCode: 'kr',
      currency: 'Korean Won',
      symbol: 'KRW',
      phpRate: 0.043,
      change: 0.001,
      changePercent: 2.33,
      lastUpdated: new Date(),
      trend: 'up'
    }
  ];

  // Initialize rates
  useEffect(() => {
    setRates(initialForexData);
  }, []);

  // Price fluctuation algorithm
  const generatePriceFluctuation = useCallback((currentRate: number): number => {
    const volatility = 0.005; // ±0.5% typical range
    const randomFactor = (Math.random() - 0.5) * 2;
    const fluctuation = currentRate * volatility * randomFactor;
    return Number((currentRate + fluctuation).toFixed(currentRate < 1 ? 4 : 2));
  }, []);

  // Update rates with realistic fluctuations
  const updateRates = useCallback(() => {
    setRates(prevRates => {
      const updatedRates = prevRates.map(rate => {
        const newRate = generatePriceFluctuation(rate.phpRate);
        const change = newRate - rate.phpRate;
        const changePercent = (change / rate.phpRate) * 100;
        
        return {
          ...rate,
          phpRate: newRate,
          change: Number(change.toFixed(rate.phpRate < 1 ? 4 : 2)),
          changePercent: Number(changePercent.toFixed(2)),
          trend: change > 0 ? 'up' as const : change < 0 ? 'down' as const : 'stable' as const,
          lastUpdated: new Date()
        };
      });

      // Trigger flash animation for changed rates
      const changedIds = updatedRates
        .filter((rate, index) => rate.phpRate !== prevRates[index].phpRate)
        .map(rate => rate.id);
      
      if (changedIds.length > 0) {
        setFlashingCells(new Set(changedIds));
        setTimeout(() => setFlashingCells(new Set()), 500);
      }

      return updatedRates;
    });
  }, [generatePriceFluctuation]);

  // Set up real-time updates
  useEffect(() => {
    const interval = setInterval(updateRates, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [updateRates]);

  const formatRate = (rate: number): string => {
    return `₱${rate.toFixed(rate < 1 ? 4 : 2)}`;
  };

  const formatChange = (change: number): string => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(change < 0.01 && change > -0.01 ? 4 : 2)}`;
  };


  const getFlashClass = (rateId: string, change: number): string => {
    if (!flashingCells.has(rateId)) return '';
    if (change > 0) return 'forex-rate-positive-flash';
    if (change < 0) return 'forex-rate-negative-flash';
    return 'forex-rate-flash';
  };

  const renderChangeIndicator = (rate: ForexRate) => {
    const isPositive = rate.change > 0;
    const isNegative = rate.change < 0;
    
    return (
      <div className={`forex-change-indicator ${
        isPositive ? 'forex-change-positive' :
        isNegative ? 'forex-change-negative' :
        'forex-change-neutral'
      }`}>
        {!isNegative && !isPositive ? null : (
          <div className={`trend-arrow ${isPositive ? 'up' : 'down'}`} />
        )}
        <span className="change-value">{formatChange(rate.change)}</span>
      </div>
    );
  };

  return (
    <div className={`forex-section ${className}`}>
      <h2 className="section-title">
        <svg className="section-icon" viewBox="0 0 20 20" fill="currentColor">
          <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zM14 6a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h8zM6 8a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 012-2h2z" />
        </svg>
        FOREX for The Day
      </h2>
      
      <div className="forex-table-container">
        <table className="forex-table">
          <thead className="forex-table-header">
            <tr>
              <th>Country</th>
              <th>Currency</th>
              <th>Symbol</th>
              <th>PHP Rate</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((rate) => (
              <tr key={rate.id} className="forex-table-row forex-table-row-enhanced">
                <td className={`forex-table-cell forex-table-cell-enhanced ${getFlashClass(rate.id, rate.change)}`}>
                  {rate.country}
                </td>
                <td className={`forex-table-cell forex-table-cell-enhanced ${getFlashClass(rate.id, rate.change)}`}>
                  {rate.currency}
                </td>
                <td className={`forex-table-cell forex-table-cell-enhanced ${getFlashClass(rate.id, rate.change)}`}>
                  {rate.symbol}
                </td>
                <td className={`forex-table-cell forex-table-cell-enhanced ${getFlashClass(rate.id, rate.change)}`}>
                  {formatRate(rate.phpRate)}
                </td>
                <td className={`forex-table-cell forex-table-cell-enhanced ${getFlashClass(rate.id, rate.change)}`}>
                  {renderChangeIndicator(rate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <div className="forex-last-updated">
          Last updated: July 7, 2025 at 2:13 PM PST
        </div>
      </div>
    </div>
  );
};

export default EnhancedForexTable;